import React, { useRef, useState } from 'react';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import { formatMoney } from '../../../shared/numberFormat';

const primaryButton = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const secondaryButton = { padding: '8px 14px', border: '1px solid #1976d2', color: '#1976d2', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const warning = { fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 10 };

// Re-estimate Shipping on Save (issue #437), shared by QuotePage.js and
// SalesOrderPage.js. Saving a document whose shipping charge came from
// Estimate Shipping, after anything the estimate was made from changed
// (estimateInputsKey: address, Service, Packaging, Options, lines), asks
// once whether to re-estimate. A charge typed over the estimate is asked
// about too. Nothing else asks: comments, PO reference and price overrides
// aren't in the key, and an invoiced charge (locked) is fixed.
//
// The key as last loaded or saved is settled (settle()), and so is one
// staff chose to Keep, so Save asks again only once the document changes
// again.
//
// confirmReestimate() resolves with what Save should send on top of the
// screen as it stands: {} to save as is (nothing to ask, or Keep), the new
// shipping_cost / shipping_estimate / estimate_packaging_id after a
// Re-estimate, or null when staff close the dialog without saving.
// requestEstimate(packagingId) prices the document as it now stands, the
// same request Estimate Shipping sends, and onReestimated(estimate,
// packagingId) puts the new estimate on screen.
export default function useReestimateOnSave({
  estimate, currentInputsKey, shippingCost, typedOver, locked,
  packagings, packagingId, requestEstimate, onReestimated, gstApplicable, documentName,
}) {
  const [settledKey, setSettledKey] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const resolveRef = useRef(null);

  const needsAsking = !!estimate && !locked
    && currentInputsKey !== estimate.inputs_key
    && currentInputsKey !== settledKey;

  const finish = (result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPrompt(null);
    if (resolve) resolve(result);
  };

  const confirmReestimate = () => {
    if (!needsAsking) return Promise.resolve({});
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setPrompt({ packagingId: packagingId || '', error: '', estimating: false });
    });
  };

  const keep = () => {
    setSettledKey(currentInputsKey);
    finish({});
  };

  const reestimate = async () => {
    setPrompt((prev) => ({ ...prev, estimating: true, error: '' }));
    try {
      const next = await requestEstimate(prompt.packagingId || null);
      onReestimated(next, prompt.packagingId);
      finish({
        shipping_cost: Number(next.charge),
        shipping_estimate: next,
        estimate_packaging_id: prompt.packagingId || null,
      });
    } catch (err) {
      setPrompt((prev) => ({ ...prev, estimating: false, error: err.response?.data?.message || 'Couldn’t estimate shipping' }));
    }
  };

  const describeCharge = () => {
    if (shippingCost === null || shippingCost === undefined) return 'none';
    return `${formatMoney(Number(shippingCost))}${gstApplicable ? ' ex GST' : ''}`;
  };
  const shownPackagings = packagings.filter((p) => p.is_active !== false || String(p.id) === String(prompt?.packagingId || ''));

  const reestimateDialog = prompt && (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !prompt.estimating) finish(null); }}
      title="Re-estimate shipping?"
      description={`The ${documentName} changed since its shipping was estimated.`}
      footer={(
        <>
          <CancelButton onClick={() => finish(null)} disabled={prompt.estimating} />
          <button type="button" style={secondaryButton} onClick={keep} disabled={prompt.estimating} title="Save the changes with the current shipping charge">
            Keep Charge
          </button>
          <button type="button" style={primaryButton} onClick={reestimate} disabled={prompt.estimating} title={`Price shipping for the ${documentName} as it now stands and save the changes with the new charge`}>
            {prompt.estimating ? 'Estimating...' : 'Re-estimate'}
          </button>
        </>
      )}
    >
      <div style={{ fontSize: 13 }}>
        Shipping charge: <strong>{describeCharge()}</strong>
        <span style={{ color: '#5a6b85' }}> · {typedOver ? 'Estimate Overridden' : 'Estimate'}</span>
      </div>
      {prompt.error && (
        <>
          <div style={warning}>{prompt.error}</div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', margin: '10px 0 4px' }}>Packaging</label>
          <select
            className="styled-input"
            value={prompt.packagingId}
            onChange={(e) => setPrompt((prev) => ({ ...prev, packagingId: e.target.value }))}
            disabled={prompt.estimating}
            style={{ fontSize: 14, width: '100%' }}
          >
            <option value="">(pick one)</option>
            {shownPackagings.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        </>
      )}
    </ConfirmDialog>
  );

  return { confirmReestimate, reestimateDialog, settle: setSettledKey };
}
